import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { StateParams, StateService } from '@uirouter/angularjs';

import {
    Alarm,
    AlarmList,
    ComponentDefinition,
    ConnectionPoint,
    Dashboard,
    DigitalSetSimpleDictionary,
    Group,
    HeaderStyle,
    Identifier,
    IGroupTreeNode,
    IValueViewModel,
    PdfExportConfiguration,
    TagDefinition,
    WidgetValues
} from '../../models';
import { getDefaultPrintOptions } from './getDefaultPrintOptions';
import { Store, StoreElement } from '../../stateManagement';
import { IRootScope } from '../../IRootScope';
import {
    IDashboardLoadService,
    IDashboardSeriesService,
    IDateRangeService,
    IExportToPdfService,
    ILoggerService,
    IPrintConfiguration,
    ISignalrService,
    IStoreService,
    IWidgetSerializationService,
    PageOption,
    pageOptions
} from '../../services';
import { IHeaderInfoService } from './headingInfo.service';
import { HeaderInfo } from './HeaderInfo';
import { getPageProperties } from './getPageProperties';
import { buildPages } from './buildPages';
import { ScaledPage } from './ScaledPage';
import { IWidget } from '../../widgets';
import { sortWidgets } from './sortWidgets';
import { backButtonClick, refreshClick, toggleEditMode, whiteBackgroundColor } from '../../common';
import { convertHeaderStyle } from './convertHeaderStyle';
import { ModernPrintPreviewContextualFormController } from 'states/printDashboard/modernPrintPreviewContextual/ModernPrintPreviewContextualFormController';
import { buildAlarmList } from '../../common/helpers/buildAlarmList';

declare type Dictionary<T> = { [guid: string]: T };

export interface PartialStore {
    alarmList: AlarmList;
    alarms: Dictionary<Alarm[]>;
    connectionPoints: Dictionary<ConnectionPoint>;
    dashboards: Dashboard[];
    digitalSetDictionary: DigitalSetSimpleDictionary;
    groups: Group[];
    identifiers: Identifier[];
    tags: Dictionary<TagDefinition>;
    valueViewModels: Dictionary<IValueViewModel>;
}

class ScaledPrintPreviewStateController implements angular.IComponentController, angular.IOnDestroy {
    dashboard: Dashboard;
    exportPdfConfig: PdfExportConfiguration;
    formController: ModernPrintPreviewContextualFormController;
    group: Group;
    headerInfo: HeaderInfo;
    isLoading: boolean;
    isReadyToPrint: boolean;
    isRequestFromAPI: boolean = false;
    isSaving: boolean;
    isTrackingDisabled: boolean;
    loadingMessage: string;
    options: PageOption[];
    pages: ScaledPage[];
    partialStore: PartialStore;
    printConfig: IPrintConfiguration;
    referenceGroup: Group;

    readonly printing: boolean;

    private _data: WidgetValues[];
    private _lang: string;
    private _printing: boolean;
    private _unsubscribe: (() => void)[] = [];
    private _unwatch: () => void;
    private _widgets: IWidget[];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$stateParams',
        '$translate',
        '$window',
        'dashboardLoadService',
        'dashboardSeriesService',
        'headerInfoService',
        'loggerService',
        'storeService',
        'widgetSerializationService',
        'exportToPdfService',
        'dateRangeService',
        'signalrService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: StateService,
        private _$stateParams: StateParams,
        private _$translate: angular.translate.ITranslateService,
        private _$window: angular.IWindowService,
        private _dashboardLoadService: IDashboardLoadService,
        private _dashboardSeriesService: IDashboardSeriesService,
        private _headerInfoService: IHeaderInfoService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService,
        private _widgetSerializationService: IWidgetSerializationService,
        private _exportToPdfService: IExportToPdfService,
        private _dateRangeService: IDateRangeService,
        private _signalrService: ISignalrService
    ) {}

    $onInit() {
        this.isReadyToPrint = false;
        this.isLoading = true;
        this._dateRangeService.initialized(false);
        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode, true);
        }
        this.options = pageOptions;

        const currentLanguage = this._$translate.use();
        let lang = this._$stateParams['lang'];
        if (!lang && !currentLanguage) {
            lang = 'en';
        }
        if (lang !== currentLanguage) {
            this._$translate.use(lang);
            moment.locale(lang);
        }
        this._lang = lang;
        this._printing = !!this._$stateParams['printing'];

        // default print options
        this.printConfig = {
            backgroundColor: whiteBackgroundColor,
            headerStyle: HeaderStyle.None,
            isHidingBorder: false,
            printOptions: getDefaultPrintOptions(),
            isUseNewPrintSystem: true
        };

        this.isTrackingDisabled = !!this.isRequestFromAPI;
        this._headerInfoService.getHeaderInfos().then((infos) => {
            this.headerInfo = infos;
            const dashboardGuid = this._$stateParams['guid'];
            const groupGuid = this._$stateParams['groupGuid'];
            this._unsubscribe.push(
                this._storeService.register(
                    (store) => this.refreshStore(store, dashboardGuid, groupGuid),
                    [
                        StoreElement.Alarms,
                        StoreElement.ConnectionPoints,
                        StoreElement.Dashboards,
                        StoreElement.DigitalSets,
                        StoreElement.Groups,
                        StoreElement.ObjectAccessRights,
                        StoreElement.Tags,
                        StoreElement.ValueViewModels
                    ]
                )
            );
        });

        this.disconnectSignalr();
        this._signalrService.connection.stateChanged((change) => this.disconnectSignalr());
        this._unsubscribe.push(this._$rootScope.$on(backButtonClick, () => this.cancel()));
    }

    $onDestroy(): void {
        this._unsubscribe.forEach((u) => u());
        if (this._unwatch) {
            this._unwatch();
        }
        this.connectSignalr();
    }

    private connectSignalr() {
        this._signalrService.connect();
    }

    private disconnectSignalr() {
        this._signalrService.disconnect();
    }

    private refreshStore(store: Store, dashboardGuid: string, groupGuid: string) {
        this.partialStore = {
            alarmList: buildAlarmList(store.alarms.dictionary, store.valueViewModels.dictionary),
            alarms: store.alarms.dictionary,
            connectionPoints: store.connectionPoints.dictionary,
            dashboards: _.values(store.dashboards.dictionary),
            digitalSetDictionary: store.digitalSetDictionary.dictionary,
            groups: _.values(store.groups.dictionary),
            identifiers: _.values(store.identifiers.dictionary),
            tags: store.tags.dictionary,
            valueViewModels: store.valueViewModels.dictionary
        };

        const dashboard = store.dashboards.dictionary[dashboardGuid];
        let title: string;
        if (dashboard) {
            this.referenceGroup = store.groups.dictionary[dashboard.referenceGroup];
            title = dashboard.name;
        }
        if (groupGuid) {
            this.group = store.groups.dictionary[groupGuid];
            if (this.group) {
                title = `${title} - ${this.group.uniqueName}`;
            }
        }
        const headerInfo = angular.copy(this.headerInfo);
        headerInfo.title = title;
        this.headerInfo = headerInfo;

        this.exportPdfConfig = {
            beginDate: this.headerInfo.beginDate.toISOString(),
            dashboard,
            endDate: this.headerInfo.endDate.toISOString(),
            group: this.group as IGroupTreeNode,
            isUseNewPrintSystem: true,
            language: this._lang,
            printOptions: this.printConfig.printOptions
        };

        if (this.dashboard !== dashboard) {
            this.dashboard = dashboard;
            const { widgets } = dashboard.configuration;
            let widgetList = widgets;
            try {
                const deserialized = widgetList.map((w) => this._widgetSerializationService.createTypedWidget(w));
                if (deserialized.length && !!deserialized[0]) {
                    widgetList = deserialized;
                }
            } catch (e) {
                console.error(e);
            }

            //Print configuration preparation
            this.preparePrintConfiguration(dashboard);
            return this.prepareWidgetList(widgetList)
                .then((widgetList) => {
                    // data loading
                    if (!this._data) {
                        return this.loadData(widgetList, headerInfo, groupGuid, dashboardGuid);
                    } else {
                        return this.bindData(this._data, widgetList);
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.isReadyToPrint = true;
                });
        }
    }

    save(): angular.IPromise<void> {
        const toSave = Object.assign({}, this.dashboard);
        toSave.configuration.printConfiguration = this.printConfig;
        toSave.configuration.widgets = this._widgets;
        this.isSaving = true;
        this.loadingMessage = 'Saving configuration';
        return this._storeService
            .dispatchers()
            .dashboards.updateOne(toSave)
            .then(() => {
                this.exportPdfConfig.printOptions = this.printConfig.printOptions;
                this.exportPdfConfig.isUseNewPrintSystem = this.printConfig.isUseNewPrintSystem;
                this.formController.$setPristine();
                this._loggerService.logSuccess('Configuration saved');
            })
            .catch(() => {
                this._loggerService.logError('Error saving print configuration');
            })
            .finally(() => (this.isSaving = false));
    }

    exportToPdf(): void {
        this._exportToPdfService.print(this.exportPdfConfig, this._$translate.use());
    }

    cancel() {
        this._$state.go('dashboard', {
            dashboardName: this.dashboard.guid,
            beginDate: this.headerInfo.beginDate.toISOString(),
            endDate: this.headerInfo.endDate.toISOString()
        });
        this._$rootScope.$broadcast(toggleEditMode, false);
    }

    print() {
        this._$window.print();
        return false;
    }

    onPrintSystemChange() {
        const url = 'printDashboard';
        this._$state.go(url, {
            guid: this._$stateParams.guid,
            groupGuid: this._$stateParams.groupGuid,
            beginDate: this._$stateParams.beginDate,
            endDate: this._$stateParams.endDate
        });
    }

    private buildPages() {
        try {
            convertHeaderStyle(this.printConfig);
            const pageProperties = getPageProperties(this.printConfig.printOptions, this.printConfig.headerStyle);
            const pages = buildPages(this._widgets, pageProperties);
            this.pages = pages;
        } catch (e) {
            console.error(e);
        }
    }

    private loadData(widgetList: IWidget[], headerInfo: HeaderInfo, groupGuid: string, dashboardGuid: string) {
        this.loadingMessage = 'Loading data';
        const bd = headerInfo.beginDate.toISOString();
        const ed = headerInfo.endDate.toISOString();
        return this._dashboardLoadService.refresh(widgetList, bd, ed, groupGuid, this.printing ?? this._printing, dashboardGuid).then((data) => {
            data = this._dashboardLoadService.setIdentifiers(data, this.partialStore.valueViewModels);
            this._data = data;
            this.bindData(data, widgetList);
            this._dateRangeService.beginDate(bd);
            this._dateRangeService.endDate(ed);
            this._dateRangeService.initialized(true);
            this._$rootScope.$broadcast(refreshClick, { beginDate: bd, endDate: ed });
            if (!this._unwatch) {
                this._unwatch = this._$scope.$watch(
                    () => this.printConfig.printOptions,
                    () => {
                        this.exportPdfConfig.printOptions = this.printConfig.printOptions;
                        this.buildPages();
                    }
                );
                this._$scope.$watchGroup([() => this.printConfig.headerStyle], () => {
                    this.buildPages();
                });
            }
        });
    }

    private bindData(data: WidgetValues[], widgetList: IWidget[]) {
        this._dashboardLoadService.assignValues(data, widgetList);
        this._widgets = widgetList;
        this.buildPages();
    }

    private preparePrintConfiguration(dashboard: Dashboard) {
        if (dashboard.configuration && dashboard.configuration.printConfiguration) {
            // work on a new object to trigger onChange
            const printConfig = angular.copy(this.printConfig);
            // retreive properties from config on new object
            Object.assign(printConfig, dashboard.configuration.printConfiguration);
            try {
                const opt = printConfig.printOptions;
                printConfig.printOptions = this.options
                    .map((o) => o.configuration)
                    .filter((o) => o.orientation === opt.orientation && o.format === opt.format)[0];
            } catch (exception) {
                printConfig.printOptions = (this.options[0] as any).configuration;
            }
            printConfig.isUseNewPrintSystem = true;
            this.printConfig = printConfig;
            this.exportPdfConfig.isUseNewPrintSystem = true;
            this.exportPdfConfig.printOptions = this.printConfig.printOptions;
        }
    }

    private prepareWidgetList(widgetList: IWidget[]): angular.IPromise<IWidget[]> {
        return this._dashboardSeriesService.getSeries(widgetList, this.referenceGroup, this.group).then((seriesHash) => {
            widgetList = this._dashboardLoadService.mapWidgetList(widgetList, seriesHash);
            widgetList.forEach((w) => {
                if (w.series) {
                    w.series.forEach((s) => {
                        delete s.attributes;
                    });
                }
            });
            this._widgets = sortWidgets(widgetList);
            this.buildPages();
            return widgetList;
        });
    }
}

export const wtScaledPrintPreviewState: ComponentDefinition = {
    componentId: 'wtScaledPrintPreviewState',
    controller: ScaledPrintPreviewStateController,
    template: require('./scaledPrintPreviewState.component.html'),
    controllerAs: 'vm',
    bindings: {
        // from route
        printing: '<'
    }
};
